import React, { useContext, useEffect, useState } from "react";
import { Input, Button, Form, App, Space } from "antd";
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from "react-router-dom";
import { FaLock, FaUserLock, FaShieldAlt } from "react-icons/fa";
import { Regex, API, Enum } from "../resources/constants";
import language from "../resources/languages/fa_IR";

// Interfaces
import { AxiosError } from "../interfaces/axios"

// Contexts
import GlobalContext from "../contexts/global";
import AxiosContext from "../contexts/axios";

// Commons
import Captcha from '../commons/captcha'

const { Pages: { ResetPassword: ResetPasswordLanguage, SignIn: SignInLanguage }, Validation, Message: MessageLanguage } = language
const { isNumber, isMobile, isEmail, isMobileOrEmail } = Regex
const { USER_MANAGEMENT_AUTH_REQUEST_RESET_PASSWORD, USER_MANAGEMENT_AUTH_RESET_PASSWORD, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message } = Enum

export default function ResetPassword() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const { token } = useParams()
    const { meta, baseURL, dispatch } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const navigate = useNavigate()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
    }, []);

  const onFinish = ({ username, password, captcha }: { username: string, password: string, captcha: string }) => {
    if (token) {
      axios.patch(USER_MANAGEMENT_AUTH_RESET_PASSWORD, { password, token, captcha }).then((response: {}) => {
        message.success(MessageLanguage[Message.CHANGE_PASSWORD]).then(response => response)
        navigate('/sign-in')
      }, async (error: AxiosError) => {
        const { response: { data } } = error;
        form.resetFields(['captcha'])
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
      })
    } else {
      axios.post(USER_MANAGEMENT_AUTH_REQUEST_RESET_PASSWORD, { username: isMobile.test(username) ? username.substring(1) : username, captcha }).then((response: {}) => {
        message.success(MessageLanguage[Message.RESET_PASSWORD_LINK]).then(response => response)
        form.resetFields()
      }, async (error: AxiosError) => {
        const { response: { data } } = error;
        form.resetFields(['captcha'])
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
      })
    }
  };

    return (
        <>
          { meta?.title && (
            <Helmet>
              <title>{ (token ? ResetPasswordLanguage.changePassword : ResetPasswordLanguage.resetPassword) + ' - ' + meta.title }</title>
            </Helmet>
          )
          }
          <Form form={form} name="reset-password" autoComplete="off" onFinish={onFinish} style={{ maxWidth: 400, margin: 'auto' }}>
            <Space direction="vertical" size="middle" style={{width:'100%'}}>
              {
                meta?.verticalLogo && (
                  <div className={'reset-password-logo'}>
                    <img alt={meta?.title} src={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + meta?.verticalLogo?.id}
                         width={250} />
                  </div>
                )
              }
              {
                token ? (
                  <>
                    <Form.Item
                      name="password"
                      rules={[
                        { required: true, message: `${ResetPasswordLanguage.password} ${Validation.required}` },
                        { min: 8, message: `${ResetPasswordLanguage.password} ${Validation.min} 8 ${Validation.length}` },
                      ]}
                    >
                      <Input.Password prefix={<FaLock size={15} />} placeholder={ResetPasswordLanguage.password} />
                    </Form.Item>
                    <Form.Item
                      name="confirm"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: `${ResetPasswordLanguage.confirm} ${Validation.required}` },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(Validation.newPasswordConfirm));
                          },
                        }),
                      ]}
                    >
                      <Input.Password prefix={<FaLock size={15} />} placeholder={ResetPasswordLanguage.confirm} />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item
                    name="username"
                    hasFeedback
                    rules={[
                      { required: true, message: `${ResetPasswordLanguage.username} ${Validation.required}` },
                      { pattern: isMobileOrEmail, message: Validation.mobileOrEmail },
                    ]}
                  >
                    <Input prefix={<FaUserLock size={15} />} placeholder={ResetPasswordLanguage.username} />
                  </Form.Item>
                )
              }
              <Form.Item
                name="captcha"
                hasFeedback
                rules={[
                  { required: true, message: `${ResetPasswordLanguage.captcha} ${Validation.required}` },
                  { pattern: isNumber, message: `${ResetPasswordLanguage.captcha} ${Validation.pattern}` },
                  { len: 4, message: `${ResetPasswordLanguage.captcha} 4 ${Validation.length}` },
                ]}
              >
                <Input type="tel" prefix={<FaShieldAlt size={15} />} maxLength={4} placeholder={ResetPasswordLanguage.captcha} addonAfter={<Captcha />} />
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    block
                    htmlType="submit"
                    disabled={(!token && !form.getFieldValue('username')) || (token && (!form.getFieldValue('password') || !form.getFieldValue('confirm'))) || !form.getFieldValue('captcha') || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                  >
                    { token ? ResetPasswordLanguage.changePassword : ResetPasswordLanguage.resetPassword }
                  </Button>
                )}
              </Form.Item>
              <Button onClick={() => navigate('/sign-in')} block>
                { SignInLanguage.signIn }
              </Button>
            </Space>
          </Form>
        </>
    )
}
